import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PenLine, Trash2, Check, AlertCircle, MapPin, Calendar, User, Lock, Eye, EyeOff } from 'lucide-react';
import { formatDateWithDay } from '../../utils/dateUtils';

/**
 * SignatureCanvasComponent v2 — Signature électronique légale
 * - Tracage fixé (touch + mouse)
 * - Champs : nom, lieu, date, adresse personnelle
 * - Validation d'erreurs complète
 * - Création de compte optionnelle (mot de passe)
 */
export default function SignatureCanvasComponent({
  onSign, onCancel, clientNom = '', clientEmail = '', showCreateAccount = true
}) {
  const sigRef = useRef(null);
  const [nom, setNom] = useState(clientNom);
  const [lieu, setLieu] = useState('');
  const [adresse, setAdresse] = useState('');
  const [dateSignature] = useState(formatDateWithDay(new Date()));
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [signing, setSigning] = useState(false);
  const [errors, setErrors] = useState({});
  // Création de compte
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  function clearSignature() {
    sigRef.current?.clear();
    setIsEmpty(true);
    setErrors(e => ({ ...e, signature: null }));
  }

  function handleBeginStroke() {
    setIsEmpty(false);
    setErrors(e => ({ ...e, signature: null }));
  }

  function validate() {
    const newErrors = {};
    if (!nom.trim()) newErrors.nom = 'Votre nom complet est requis';
    if (!lieu.trim()) newErrors.lieu = 'Le lieu de signature est requis';
    if (!adresse.trim()) newErrors.adresse = 'Votre adresse personnelle est requise';
    if (!cgvAccepted) newErrors.cgv = 'Vous devez accepter les CGV pour signer';
    if (!sigRef.current || sigRef.current.isEmpty()) newErrors.signature = 'Veuillez tracer votre signature dans le cadre';
    if (createAccount) {
      if (!password) newErrors.password = 'Mot de passe requis';
      else if (password.length < 8) newErrors.password = 'Minimum 8 caractères';
      else if (!/[A-Z]/.test(password)) newErrors.password = 'Au moins une majuscule requise';
      if (password !== passwordConfirm) newErrors.passwordConfirm = 'Les mots de passe ne correspondent pas';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleConfirm() {
    if (!validate()) return;
    setSigning(true);
    try {
      const signatureData = sigRef.current.toDataURL('image/png');
      const now = new Date();
      await onSign({
        signature: signatureData,
        nom: nom.trim(),
        lieu: lieu.trim(),
        adresse: adresse.trim(),
        cgv_accepted: true,
        timestamp: now.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        create_account: createAccount,
        password: createAccount ? password : undefined,
      });
    } finally {
      setSigning(false);
    }
  }

  const canConfirm = nom.trim() && lieu.trim() && adresse.trim() && cgvAccepted && !isEmpty;

  return (
    <div className="space-y-4">
      {/* Date auto-remplie */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
        <Calendar size={14} style={{ color: '#D4AF37', flexShrink: 0 }} />
        <p className="text-xs font-medium" style={{ color: '#D4AF37' }}>Signature du {dateSignature}</p>
      </div>

      {/* Nom complet */}
      <FieldSig
        label="Nom complet du signataire *"
        icon={<User size={13}/>}
        value={nom}
        onChange={e => { setNom(e.target.value); setErrors(er => ({ ...er, nom: null })); }}
        placeholder="Ex : Jean Dupont"
        error={errors.nom}
      />

      {/* Lieu de signature */}
      <FieldSig
        label="Lieu de signature *"
        icon={<MapPin size={13}/>}
        value={lieu}
        onChange={e => { setLieu(e.target.value); setErrors(er => ({ ...er, lieu: null })); }}
        placeholder="Ex : Figeac, Lot"
        error={errors.lieu}
      />

      {/* Adresse personnelle */}
      <FieldSig
        label="Votre adresse personnelle *"
        icon={<MapPin size={13}/>}
        value={adresse}
        onChange={e => { setAdresse(e.target.value); setErrors(er => ({ ...er, adresse: null })); }}
        placeholder="Ex : 12 rue de la Paix, 46100 Figeac"
        error={errors.adresse}
      />

      {/* Canvas de signature */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <PenLine size={12}/> Signez ici (doigt ou souris) *
          </label>
          <button onClick={clearSignature}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
            style={{ color: 'rgba(255,92,108,0.7)', background: 'rgba(255,92,108,0.08)' }}>
            <Trash2 size={12}/> Effacer
          </button>
        </div>

        <div className="rounded-xl overflow-hidden" style={{
          border: errors.signature ? '2px solid rgba(255,92,108,0.6)' : isEmpty ? '2px dashed rgba(212,175,55,0.4)' : '2px solid rgba(212,175,55,0.7)',
          background: '#FFFFFF', position: 'relative', cursor: 'crosshair',
        }}>
          <SignatureCanvas
            ref={sigRef}
            canvasProps={{
              style: { display: 'block', touchAction: 'none', width: '100%', height: 120 },
              className: 'w-full'
            }}
            backgroundColor="#FFFFFF"
            penColor="#0D0D0D"
            onBegin={handleBeginStroke}
          />
          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ color: 'rgba(0,0,0,0.25)', fontSize: 13, fontFamily: 'Manrope, sans-serif', flexDirection: 'column', gap: 4 }}>
              <PenLine size={20} style={{ opacity: 0.3 }}/>
              <span>Tracez votre signature ici</span>
            </div>
          )}
        </div>

        {/* Erreur signature */}
        {errors.signature && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <AlertCircle size={12} style={{ color: '#FF5C6C' }}/>
            <p className="text-xs" style={{ color: '#FF5C6C' }}>{errors.signature}</p>
          </div>
        )}
        <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Signature horodatée et enregistrée avec votre IP pour valeur légale.
        </p>
      </div>

      {/* Option créer un compte */}
      {showCreateAccount && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(93,188,191,0.2)', background: 'rgba(93,188,191,0.04)' }}>
          <label className="flex items-start gap-3 p-4 cursor-pointer">
            <input type="checkbox" checked={createAccount} onChange={e => setCreateAccount(e.target.checked)}
              className="mt-0.5 flex-shrink-0" style={{ accentColor: '#5DBCBF' }}/>
            <div>
              <p className="text-xs font-semibold" style={{ color: '#5DBCBF' }}>Créer mon espace client</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                Suivez vos devis, téléchargez vos factures et accédez à votre programme fidélité.
              </p>
            </div>
          </label>
          {createAccount && (
            <div className="px-4 pb-4 space-y-3">
              <FieldSig
                label="Mot de passe *"
                icon={<Lock size={13}/>}
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setErrors(er => ({ ...er, password: null })); }}
                placeholder="Min. 8 caractères, 1 majuscule"
                error={errors.password}
                suffix={
                  <button type="button" onClick={() => setShowPwd(s => !s)} style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
                  </button>
                }
              />
              <FieldSig
                label="Confirmer le mot de passe *"
                icon={<Lock size={13}/>}
                type={showPwd ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={e => { setPasswordConfirm(e.target.value); setErrors(er => ({ ...er, passwordConfirm: null })); }}
                placeholder="Retapez votre mot de passe"
                error={errors.passwordConfirm}
              />
            </div>
          )}
        </div>
      )}

      {/* CGV */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={cgvAccepted} onChange={e => { setCgvAccepted(e.target.checked); setErrors(er => ({ ...er, cgv: null })); }}
            className="mt-0.5 flex-shrink-0" style={{ accentColor: '#D4AF37' }}/>
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
            J'accepte les{' '}
            <span style={{ color: '#D4AF37', textDecoration: 'underline', cursor: 'pointer' }}>
              Conditions Générales de Vente
            </span>{' '}
            de Show Événement. Je reconnais avoir pris connaissance du devis et l'approuve dans son intégralité.
            Cette signature a valeur contractuelle conformément à la loi.
          </span>
        </label>
        {errors.cgv && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <AlertCircle size={12} style={{ color: '#FF5C6C' }}/>
            <p className="text-xs" style={{ color: '#FF5C6C' }}>{errors.cgv}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        {onCancel && (
          <button onClick={onCancel}
            className="px-4 py-2.5 rounded-xl text-sm font-medium"
            style={{ background: '#2A2A2A', color: 'rgba(255,255,255,0.6)' }}>
            ← Retour
          </button>
        )}
        <button onClick={handleConfirm} disabled={signing}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 justify-center"
          style={{
            background: canConfirm && !signing ? '#D4AF37' : 'rgba(212,175,55,0.3)',
            color: '#0D0D0D',
            cursor: canConfirm && !signing ? 'pointer' : 'not-allowed'
          }}>
          <Check size={16}/>
          {signing ? 'Signature en cours...' : 'Confirmer ma signature'}
        </button>
      </div>

      {/* Résumé des erreurs si tentative invalid */}
      {Object.values(errors).some(Boolean) && (
        <div className="p-3 rounded-xl" style={{ background: 'rgba(255,92,108,0.08)', border: '1px solid rgba(255,92,108,0.2)' }}>
          <p className="text-xs font-semibold mb-1" style={{ color: '#FF5C6C' }}>Veuillez corriger les erreurs :</p>
          {Object.values(errors).filter(Boolean).map((e, i) => (
            <p key={i} className="text-xs" style={{ color: 'rgba(255,92,108,0.8)' }}>• {e}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldSig({ label, icon, value, onChange, placeholder, error, type = 'text', suffix }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{icon}</span>{label}
      </label>
      <div className="relative">
        <input type={type} placeholder={placeholder} value={value} onChange={onChange}
          className="w-full px-3 py-2.5 rounded-xl text-sm"
          style={{
            background: '#1C1C1C',
            border: `1px solid ${error ? 'rgba(255,92,108,0.5)' : '#2A2A2A'}`,
            color: '#FFFFFF', outline: 'none',
            paddingRight: suffix ? 36 : undefined
          }} />
        {suffix && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>
        )}
      </div>
      {error && (
        <div className="flex items-center gap-1.5 mt-1">
          <AlertCircle size={11} style={{ color: '#FF5C6C' }}/>
          <p className="text-xs" style={{ color: '#FF5C6C' }}>{error}</p>
        </div>
      )}
    </div>
  );
}
